import { useState } from "react";
import { CircleNotch, IdentificationBadge, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function BulkBadgePhotos({ staff }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState("");
  const [assign, setAssign] = useState({});
  const [removeBg, setRemoveBg] = useState(false);

  const prepare = async () => {
    if (!files.length) { toast.error("Pick photos first"); return; }
    setBusy(true);
    setBatch(null);
    setItems([]);
    setProgress(`0 / ${files.length}`);
    const CHUNK = removeBg ? 2 : 6;
    let bid = "";
    let matched = 0;
    let all = [];
    try {
      for (let i = 0; i < files.length; i += CHUNK) {
        const fd = new FormData();
        files.slice(i, i + CHUNK).forEach((f) => fd.append("files", f));
        if (removeBg) fd.append("remove_bg", "1");
        if (bid) fd.append("batch_id", bid);
        const { data } = await api.post("/admin/badge-photos/prepare", fd, { timeout: 300000 });
        bid = data.batch_id;
        all = [...all, ...data.items];
        setItems(all);
        setAssign((cur) => {
          const next = { ...cur };
          data.items.forEach((it) => { if (it.suggested_user_id) next[`${it.batch_id}:${it.index}`] = it.suggested_user_id; });
          return next;
        });
        setProgress(`${Math.min(i + CHUNK, files.length)} / ${files.length}`);
      }
      matched = all.filter((it) => it.suggested_user_id).length;
      toast.success(`${all.length} photo(s) cropped & cleaned — ${matched} matched by filename`);
      setBatch({ batch_id: bid });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); setProgress(""); }
  };

  const commit = async () => {
    const assignments = items.filter((it) => !it.error && assign[`${it.batch_id}:${it.index}`])
      .map((it) => ({ batch_id: it.batch_id, index: it.index, user_id: assign[`${it.batch_id}:${it.index}`] }));
    if (!assignments.length) { toast.error("Assign at least one photo to an employee"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/badge-photos/commit", { batch_id: batch.batch_id, assignments });
      toast.success(`Saved ${data.saved} badge photo(s)`);
      setBatch(null);
      setItems([]);
      setFiles([]);
      setAssign({});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="bulk-badge-card">
      <div className="flex items-center gap-2">
        <IdentificationBadge size={18} className="text-primary" />
        <p className="font-heading text-sm font-semibold text-stone-900">Bulk badge photos</p>
      </div>
      <p className="mt-0.5 max-w-2xl text-xs text-stone-400">
        Drop raw photos or license screenshots — the app finds the face, crops to badge shape, cleans them up,
        and guesses the employee from the filename. You confirm who's who; nothing saves until you do.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-dashed border-stone-300 px-3 py-2 text-xs font-medium text-stone-700 hover:border-primary" data-testid="bulk-badge-input">
          <UploadSimple size={14} /> {files.length ? `${files.length} photo(s) picked` : "Pick photos (multiple)"}
          <input type="file" accept="image/*" multiple className="hidden" data-testid="bulk-badge-file-input"
            onChange={(e) => setFiles(Array.from(e.target.files || []))} />
        </label>
        <button onClick={prepare} disabled={busy || !files.length}
          className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-50" data-testid="bulk-badge-prepare">
          {busy ? <span className="inline-flex items-center gap-1.5"><CircleNotch size={14} className="animate-spin" /> {progress || "…"}</span> : "Crop & clean"}
        </button>
        <label className="flex items-center gap-1.5 text-xs text-stone-600" data-testid="bulk-badge-removebg-toggle">
          <input type="checkbox" checked={removeBg} onChange={(e) => setRemoveBg(e.target.checked)} className="accent-[#6a3e97]" />
          Remove backgrounds with AI (slower — a few seconds per photo)
        </label>
      </div>

      {batch && items.length > 0 && (
        <div className="mt-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {items.map((it) => (
              <div key={`${it.batch_id}:${it.index}`} className="rounded-lg border border-stone-200 p-2" data-testid={`bulk-badge-item-${it.index}`}>
                {it.error ? (
                  <p className="py-6 text-center text-xs text-[#C0392B]">{it.filename}: {it.error}</p>
                ) : (
                  <>
                    <img src={`${api.defaults.baseURL}/admin/badge-photos/preview/${it.batch_id}/${it.index}`}
                      alt={it.filename} className="aspect-[4/5] w-full rounded-md object-cover" data-testid={`bulk-badge-preview-${it.index}`} />
                    <p className="mt-1 truncate text-[10px] text-stone-400">{it.filename}</p>
                    <select value={assign[`${it.batch_id}:${it.index}`] || ""} onChange={(e) => setAssign((s) => ({ ...s, [`${it.batch_id}:${it.index}`]: e.target.value }))}
                      className="mt-1 h-8 w-full rounded-md border border-stone-300 bg-white px-1.5 text-xs" data-testid={`bulk-badge-select-${it.index}`}>
                      <option value="">— pick employee —</option>
                      {staff.map((s) => <option key={s.id || s._id} value={s.id || s._id}>{s.name || s.email}</option>)}
                    </select>
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button onClick={commit} disabled={busy}
              className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-50" data-testid="bulk-badge-commit">
              {busy ? "Saving…" : `Save badge photos (${items.filter((it) => !it.error && assign[`${it.batch_id}:${it.index}`]).length} assigned)`}
            </button>
            <button onClick={() => { setBatch(null); setItems([]); setFiles([]); setAssign({}); }} className="text-xs text-stone-500 hover:underline" data-testid="bulk-badge-cancel">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
